import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

let drawerSequence = 0;

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} menuDrawer
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #controller;
  #frame;
  #accordionTimer;
  #accordionNodes = new Set();
  #operation = 0;
  #desiredOpen = false;
  #trapOwned = false;
  #layoutObserver;
  #openedInFlow = false;
  #openedAsOverlay = false;
  #scrollLockToken = `header-drawer-${++drawerSequence}`;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    const previousDetails = this.refs.details;
    const desiredOpen = this.#desiredOpen;
    this.#cancelResources();
    super.updatedCallback();
    this.#bind(previousDetails === this.refs.details ? desiredOpen : this.isOpen);
  }

  disconnectedCallback() {
    this.#cancelResources();
    this.#resetRoot();
    super.disconnectedCallback();
  }

  #bind(desiredOpen = this.isOpen) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // JS owns its lock; the native attribute remains as the no-JS fallback only.
    this.refs.details.removeAttribute('scroll-lock');
    this.#desiredOpen = desiredOpen;
    if (!desiredOpen) reset(this.refs.details);
    this.refs.details.classList.toggle('menu-open', this.#desiredOpen);
    this.refs.menuDrawer.inert = !this.#desiredOpen;
    this.setAttribute('data-drawer-ready', '');
    this.addEventListener('animationend', removeWillChangeOnAnimationEnd, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onOutside, { capture: true, signal });
    document.addEventListener('click', this.#onOutside, { capture: true, signal });
    document.addEventListener('focusin', this.#onOutside, { capture: true, signal });
    window.addEventListener('resize', this.#onLayoutChange, { signal });
    window.visualViewport?.addEventListener('resize', this.#onLayoutChange, { signal });
    const header = this.closest('header-component');
    if (header) {
      this.#layoutObserver = new MutationObserver(this.#onLayoutChange);
      this.#layoutObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
    }
    if (this.#desiredOpen) {
      this.#openedInFlow = this.#inFlow;
      this.#openedAsOverlay = this.#overlay;
      this.#updatePanelBounds();
      this.#lockScroll();
      // Section updates must restore the active nested pane's focus boundary.
      const nested = this.#activePane();
      if (nested || !this.#inFlow) {
        trapFocus(nested ?? this.#rootFocusScope);
        this.#trapOwned = true;
      }
    }
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  get #inFlow() {
    return getComputedStyle(this.refs.menuDrawer).getPropertyValue('--menu-drawer-in-flow').trim() === '1';
  }

  get #overlay() {
    return getComputedStyle(this.refs.menuDrawer).getPropertyValue('--menu-drawer-overlay').trim() === '1';
  }

  get #rootFocusScope() {
    return this.#overlay ? this.closest('header-component') ?? this : this;
  }

  /** @param {Event} [event] */
  #getDetailsElement(event) {
    return event?.target instanceof Element ? event.target.closest('details') ?? this.refs.details : this.refs.details;
  }

  /** @param {Event} [event] */
  toggle(event) {
    event?.preventDefault();
    return this.#desiredOpen ? this.close() : this.open();
  }

  /** @param {string} [target] @param {Event} [event] */
  open(target, event) {
    event?.preventDefault();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary || (details !== this.refs.details && !this.#desiredOpen)) return;
    const operation = ++this.#operation;
    this.#cancelFrame();
    if (details === this.refs.details) {
      this.#desiredOpen = true;
      this.#openedInFlow = this.#inFlow;
      this.#openedAsOverlay = this.#overlay;
      this.refs.menuDrawer.inert = false;
      this.#updatePanelBounds();
      this.#lockScroll();
    }
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details === this.refs.details ? this : details);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      if (!this.isConnected || operation !== this.#operation || !this.#desiredOpen) return;
      details.classList.add('menu-open');
      if (target) this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      const panel = this.#panelFor(details);
      onAnimationEnd(panel, () => {
        if (!this.isConnected || operation !== this.#operation || !this.#desiredOpen) return;
        if (details !== this.refs.details || !this.#inFlow) {
          // Include the visible toolbar in the overlay's keyboard path.
          trapFocus(details === this.refs.details ? this.#rootFocusScope : details);
          this.#trapOwned = true;
        }
      }, { subtree: false });
    });
  }

  /** @param {Event} [event] */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /** @param {HTMLDetailsElement} details */
  #panelFor(details) {
    return details === this.refs.details ? this.refs.menuDrawer : details.querySelector('.menu-drawer__submenu') ?? details;
  }

  #activePane() {
    return [...this.querySelectorAll('details[open]:not(accordion-custom > details)')]
      .filter((details) => details !== this.refs.details).at(-1);
  }

  /** @param {HTMLDetailsElement} details @param {boolean} [restoreFocus] */
  #close(details, restoreFocus = true) {
    const summary = details.querySelector('summary');
    if (!summary) return;
    const operation = ++this.#operation;
    this.#cancelFrame();
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    if (details === this.refs.details) {
      this.#desiredOpen = false;
      this.refs.menuDrawer.inert = true;
      this.#releaseTrap();
      this.#unlockScroll();
    }
    onAnimationEnd(this.#panelFor(details), () => {
      if (!this.isConnected || operation !== this.#operation) return;
      reset(details);
      if (details === this.refs.details) {
        this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
      } else {
        this.#releaseTrap();
        const activePane = this.#activePane();
        this.refs.menuDrawer.classList.toggle('menu-drawer--has-submenu-opened', Boolean(activePane));
        if (this.#desiredOpen && (activePane || !this.#inFlow)) {
          trapFocus(activePane ?? this.#rootFocusScope);
          this.#trapOwned = true;
        }
      }
      if (restoreFocus && summary.isConnected) summary.focus({ preventScroll: true });
    }, { subtree: false });
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#desiredOpen) return;
    event.preventDefault();
    this.#close(this.#getDetailsElement(event));
  };

  #onOutside = (event) => {
    const scope = event.type === 'focusin' ? this.#rootFocusScope : this;
    if (this.#desiredOpen && event.target instanceof Node && !scope.contains(event.target)) this.#close(this.refs.details, false);
  };

  #onLayoutChange = () => {
    if (!this.#desiredOpen) return;
    if (this.#openedInFlow !== this.#inFlow || this.#openedAsOverlay !== this.#overlay) this.#close(this.refs.details, false);
    else this.#updatePanelBounds();
  };

  #updatePanelBounds() {
    const header = this.closest('header-component');
    const columns = header?.querySelector('.header__row--top .header__columns');
    if (!columns) return;
    const toolbarHeight = Number.parseFloat(getComputedStyle(header).getPropertyValue('--mobile-toolbar-height')) || 64;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const available = Math.max(0, viewportHeight - columns.getBoundingClientRect().top - toolbarHeight);
    this.refs.menuDrawer.style.setProperty('--mobile-menu-max-height', `${available}px`);
  }

  #lockScroll() {
    if (!this.#inFlow && !document.documentElement.hasAttribute('scroll-lock')) document.documentElement.setAttribute('scroll-lock', this.#scrollLockToken);
  }

  #unlockScroll() {
    if (document.documentElement.getAttribute('scroll-lock') === this.#scrollLockToken) document.documentElement.removeAttribute('scroll-lock');
  }

  #releaseTrap() {
    if (this.#trapOwned) removeTrapFocus();
    this.#trapOwned = false;
  }

  #cancelFrame() {
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  /** @param {HTMLElement} scope */
  preventInitialAccordionAnimations(scope) {
    this.#clearAccordionTimer();
    scope.querySelectorAll('accordion-custom .details-content').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      this.#accordionNodes.add(element);
      element.classList.add('details-content--no-animation');
    });
    this.#accordionTimer = setTimeout(() => this.#clearAccordionTimer(), 100);
  }

  #clearAccordionTimer() {
    clearTimeout(this.#accordionTimer);
    this.#accordionTimer = undefined;
    this.#accordionNodes.forEach((element) => element.classList.remove('details-content--no-animation'));
    this.#accordionNodes.clear();
  }

  #resetRoot() {
    if (!this.refs.details) return;
    reset(this.refs.details);
    this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
    this.#desiredOpen = false;
    this.refs.menuDrawer.inert = true;
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
  }

  #cancelResources() {
    ++this.#operation;
    this.#controller?.abort();
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = undefined;
    this.#cancelFrame();
    this.#clearAccordionTimer();
    this.#releaseTrap();
    this.#unlockScroll();
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);

/** @param {HTMLDetailsElement} element */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
